import {
  BarChart3,
  BellRing,
  BookOpen,
  CalendarCheck,
  Banknote,
  FileCheck2,
  FileText,
  Flag,
  Home,
  Import,
  LayoutDashboard,
  ListChecks,
  MessageCircle,
  Network,
  ClipboardCheck,
  Percent,
  Receipt,
  ReceiptText,
  Wallet,
  SlidersHorizontal,
  Gauge,
  Sparkles,
  Settings,
  Shuffle,
  Target,
  User,
  Users,
  UserSquare2,
  Waypoints,
} from "lucide-react";
import { CanAccess, useGetIdentity, useTranslate, useUserMenu } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";
import { RefreshButton } from "@/components/admin/refresh-button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

import { AlmikiaPage } from "../almikia/AlmikiaPage";
import { ExpenseMonthlyTotals, ExpenseReimbursements } from "../expenses";
import { FeedbackButton } from "../feedback/FeedbackButton";
import { GoalsPage } from "../goals/GoalsPage";
import { VoboPendingPage } from "../invoicing";
import { canAccessPendingVobo } from "../providers/commons/canAccess";
import type { AppRole } from "../types";
import { AssignmentLog, MyAvailability } from "../leadAssignment";
import {
  PlanTemplateList,
  PlansToApprove,
  QuoterConfigPage,
  QuoterPanel,
} from "../quoter";
import { SalesToolLibrary } from "../salesTools";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { CampaignReport } from "../metaLeads/CampaignReport";
import { InboxScreen } from "../whatsapp/InboxScreen";
import { getPayablesNavLabelKey } from "./navEntries";
import { ImportPage } from "../misc/ImportPage";
import { ChangelogPage } from "../misc/ChangelogPage";

/**
 * Desktop navy left rail (mockups B/C): the Almikia mark on top, one entry
 * per existing destination gated by the same `CanAccess` checks as before,
 * and the theme toggle / refresh action / user menu pinned to the bottom.
 * Replaces the previous top navy header — the page area now sits beside the
 * rail, on the paper background.
 */
const Header = () => {
  const { darkModeLogo, lightModeLogo, title } = useConfigurationContext();
  const location = useLocation();
  const translate = useTranslate();
  const { identity } = useGetIdentity();

  let currentPath: string | boolean = "/";
  if (matchPath("/", location.pathname)) {
    currentPath = "/";
  } else if (matchPath("/contacts/*", location.pathname)) {
    currentPath = "/contacts";
  } else if (matchPath("/deals/*", location.pathname)) {
    currentPath = "/deals";
  } else if (matchPath("/projects/*", location.pathname)) {
    currentPath = "/projects";
  } else if (matchPath("/clients/*", location.pathname)) {
    currentPath = "/clients";
  } else if (matchPath("/operations/*", location.pathname)) {
    currentPath = "/operations";
  } else if (matchPath("/receivables/*", location.pathname)) {
    currentPath = "/receivables";
  } else if (matchPath("/payables/*", location.pathname)) {
    currentPath = "/payables";
  } else if (
    matchPath("/expenses/*", location.pathname) ||
    matchPath("/expense_categories/*", location.pathname) ||
    matchPath(ExpenseMonthlyTotals.path, location.pathname) ||
    matchPath(ExpenseReimbursements.path, location.pathname)
  ) {
    currentPath = "/expenses";
  } else if (matchPath(VoboPendingPage.path, location.pathname)) {
    currentPath = VoboPendingPage.path;
  } else if (matchPath("/property_options/*", location.pathname)) {
    currentPath = "/property_options";
  } else if (matchPath("/almikia/*", location.pathname)) {
    currentPath = "/almikia";
  } else if (matchPath(GoalsPage.path, location.pathname)) {
    currentPath = GoalsPage.path;
  } else if (matchPath("/incentive_programs/*", location.pathname)) {
    currentPath = "/incentive_programs";
  } else if (matchPath("/deal_stage_configs/*", location.pathname)) {
    currentPath = "/deal_stage_configs";
  } else if (matchPath("/deal_activity_types/*", location.pathname)) {
    currentPath = "/deal_activity_types";
  } else if (matchPath("/alert_rules/*", location.pathname)) {
    currentPath = "/alert_rules";
  } else if (matchPath("/ai_usage", location.pathname)) {
    currentPath = "/ai_usage";
  } else if (matchPath("/lead_assignment", location.pathname)) {
    currentPath = "/lead_assignment";
  } else if (matchPath(MyAvailability.path, location.pathname)) {
    currentPath = MyAvailability.path;
  } else if (matchPath(AssignmentLog.path, location.pathname)) {
    currentPath = AssignmentLog.path;
  } else if (matchPath(SalesToolLibrary.path, location.pathname)) {
    currentPath = SalesToolLibrary.path;
  } else if (matchPath(PlanTemplateList.path, location.pathname)) {
    currentPath = PlanTemplateList.path;
  } else if (matchPath(QuoterConfigPage.path, location.pathname)) {
    currentPath = QuoterConfigPage.path;
  } else if (matchPath(QuoterPanel.path, location.pathname)) {
    currentPath = QuoterPanel.path;
  } else if (matchPath(PlansToApprove.path, location.pathname)) {
    currentPath = PlansToApprove.path;
  } else if (matchPath(CampaignReport.path, location.pathname)) {
    currentPath = CampaignReport.path;
  } else if (matchPath(InboxScreen.path, location.pathname)) {
    currentPath = InboxScreen.path;
  } else {
    currentPath = false;
  }

  return (
    <header className="bg-sidebar text-sidebar-foreground [--heading:var(--sidebar-foreground)] flex flex-col items-stretch w-56 shrink-0 h-screen sticky top-0">
      <Link
        to="/"
        className="flex items-center gap-2 px-4 py-4 text-sidebar-foreground no-underline shrink-0"
      >
        <img
          className="[.light_&]:hidden h-6 shrink-0"
          src={darkModeLogo}
          alt={title}
        />
        <img
          className="[.dark_&]:hidden h-6 shrink-0"
          src={lightModeLogo}
          alt={title}
        />
        <span className="text-lg font-semibold truncate">{title}</span>
      </Link>
      <nav className="flex flex-col gap-1 px-2 flex-1 overflow-y-auto">
        <RailLink
          to="/"
          label={translate("ra.page.dashboard")}
          Icon={LayoutDashboard}
          isActive={currentPath === "/"}
        />
        <CanAccess resource="contacts" action="list">
          <RailLink
            to="/contacts"
            label={translate("resources.contacts.name", { smart_count: 2 })}
            Icon={Users}
            isActive={currentPath === "/contacts"}
          />
        </CanAccess>
        <CanAccess resource="deals" action="list">
          <RailLink
            to="/deals"
            label={translate("resources.deals.name", { smart_count: 2 })}
            Icon={Target}
            isActive={currentPath === "/deals"}
          />
        </CanAccess>
        <CanAccess resource="projects" action="list">
          <RailLink
            to="/projects"
            label={translate("resources.projects.name", { smart_count: 2 })}
            Icon={FileText}
            isActive={currentPath === "/projects"}
          />
        </CanAccess>
        <CanAccess resource="clients" action="list">
          <RailLink
            to="/clients"
            label={translate("resources.clients.name", { smart_count: 2 })}
            Icon={UserSquare2}
            isActive={currentPath === "/clients"}
          />
        </CanAccess>
        {/* Almikia Epic E4 (TASK-027): every role but contabilidad. */}
        <CanAccess resource="operations" action="list">
          <RailLink
            to="/operations"
            label={translate("resources.operations.name", { smart_count: 2 })}
            Icon={ClipboardCheck}
            isActive={currentPath === "/operations"}
          />
        </CanAccess>
        {/* Almikia Epic E6 (TASK-040): direccion only ("Pendientes de VoBo"). */}
        {identity && canAccessPendingVobo(identity.role as AppRole) ? (
          <RailLink
            to={VoboPendingPage.path}
            label={translate("crm.invoicing.vobo.nav")}
            Icon={FileCheck2}
            isActive={currentPath === VoboPendingPage.path}
          />
        ) : null}
        {/* Almikia Epic E6 (TASK-040): direccion, contabilidad, auditoria. */}
        <CanAccess resource="receivables" action="list">
          <RailLink
            to="/receivables"
            label={translate("resources.receivables.name", { smart_count: 2 })}
            Icon={Receipt}
            isActive={currentPath === "/receivables"}
          />
        </CanAccess>
        {/* Almikia Epic E6 (TASK-041): "Cuentas por pagar" (the list itself
            shows "Mis pagos" to an asesora / lider). */}
        <CanAccess resource="payables" action="list">
          <RailLink
            to="/payables"
            label={translate(
              getPayablesNavLabelKey(identity?.role as AppRole),
              { smart_count: 2 },
            )}
            Icon={Banknote}
            isActive={currentPath === "/payables"}
          />
        </CanAccess>
        {/* Almikia Epic EG (TASK-044): "Gastos" for direccion, lead_manager,
            administracion and contabilidad only (mirrors the expenses RLS). */}
        <CanAccess resource="expenses" action="list">
          <RailLink
            to="/expenses"
            label={translate("resources.expenses.name", { smart_count: 2 })}
            Icon={ReceiptText}
            isActive={currentPath === "/expenses"}
          />
        </CanAccess>
        {/* Almikia Epic EC (TASK-030): every role but contabilidad. */}
        <CanAccess resource="property_options" action="list">
          <RailLink
            to="/property_options"
            label={translate("resources.property_options.name", {
              smart_count: 2,
            })}
            Icon={Home}
            isActive={currentPath === "/property_options"}
          />
        </CanAccess>
        {/* Almikia Epic EO (TASK-098): every one of the 8 roles reaches the
            Almikia section (org chart, and culture later, TASK-099). */}
        <CanAccess resource="org_chart_people" action="list">
          <RailLink
            to={AlmikiaPage.path}
            label={translate("crm.almikia.nav_label")}
            Icon={Network}
            isActive={currentPath === "/almikia"}
          />
        </CanAccess>
        {/* Almikia Epic E7 (TASK-091): direccion, lead_manager and lider
            only -- mirrors TASK-088's `canAccess("advisor_goals", "list")`. */}
        <CanAccess resource="advisor_goals" action="list">
          <RailLink
            to={GoalsPage.path}
            label={translate("crm.goals.title")}
            Icon={Flag}
            isActive={currentPath === GoalsPage.path}
          />
        </CanAccess>
        {/* Almikia Epic E7 (TASK-095): direccion only -- mirrors
            `canAccess("incentive_programs", "edit")` (every role can list
            the catalogue, only direccion writes it, so this must gate on
            "edit", not "list"). */}
        <CanAccess resource="incentive_programs" action="edit">
          <RailLink
            to="/incentive_programs"
            label={translate("resources.incentive_programs.name", {
              smart_count: 2,
            })}
            Icon={Percent}
            isActive={currentPath === "/incentive_programs"}
          />
        </CanAccess>
        {/* Almikia Epic EP (TASK-105): direccion only -- mirrors
            `canAccess("deal_stage_configs", "edit")` (every role reads the
            configured stages, only direccion writes them, so this must gate
            on "edit", not "list"). */}
        <CanAccess resource="deal_stage_configs" action="edit">
          <RailLink
            to="/deal_stage_configs"
            label={translate("resources.deal_stage_configs.name", {
              smart_count: 2,
            })}
            Icon={Waypoints}
            isActive={currentPath === "/deal_stage_configs"}
          />
        </CanAccess>
        {/* Almikia Epic EA (TASK-113): direccion only -- mirrors
            `canAccess("deal_activity_types", "edit")` (every role reads the
            activity catalogue, only direccion writes it, so this must gate
            on "edit", not "list"). Placed next to "Etapas". */}
        <CanAccess resource="deal_activity_types" action="edit">
          <RailLink
            to="/deal_activity_types"
            label={translate("resources.deal_activity_types.name", {
              smart_count: 2,
            })}
            Icon={ListChecks}
            isActive={currentPath === "/deal_activity_types"}
          />
        </CanAccess>
        {/* Almikia Epic ER (TASK-006): direccion only -- mirrors
            `canAccess("alert_rules", "edit")` (every role reads the alert
            rule catalogue, only direccion writes it, so this must gate on
            "edit", not "list"). Placed next to "Etapas"/"Actividades". */}
        <CanAccess resource="alert_rules" action="edit">
          <RailLink
            to="/alert_rules"
            label={translate("resources.alert_rules.name", {
              smart_count: 2,
            })}
            Icon={BellRing}
            isActive={currentPath === "/alert_rules"}
          />
        </CanAccess>
        {/* Almikia Epic EI (TASK-005): direccion only (`ai_usage`). */}
        <CanAccess resource="ai_usage" action="list">
          <RailLink
            to="/ai_usage"
            label={translate("crm.aiUsage.nav")}
            Icon={Sparkles}
            isActive={currentPath === "/ai_usage"}
          />
        </CanAccess>
        {/* Almikia Epic EL (TASK-008): direccion only (`lead_assignment`). */}
        <CanAccess resource="lead_assignment" action="list">
          <RailLink
            to="/lead_assignment"
            label={translate("crm.leadAssignment.nav")}
            Icon={Shuffle}
            isActive={currentPath === "/lead_assignment"}
          />
        </CanAccess>
        {/* Almikia Epic EL (TASK-009): asesora and lider only. */}
        <CanAccess resource="advisor_availability" action="list">
          <RailLink
            to={MyAvailability.path}
            label={translate("crm.availability.nav")}
            Icon={CalendarCheck}
            isActive={currentPath === MyAvailability.path}
          />
        </CanAccess>
        {/* Almikia Epic EL (TASK-010): direccion, lead_manager, asesora, lider. */}
        <CanAccess resource="lead_assignment_log" action="list">
          <RailLink
            to={AssignmentLog.path}
            label={translate("crm.assignmentLog.nav")}
            Icon={ListChecks}
            isActive={currentPath === AssignmentLog.path}
          />
        </CanAccess>
        {/* Almikia Epic EM (TASK-068): direccion and lead_manager only. */}
        <CanAccess resource="meta_campaign_report" action="list">
          <RailLink
            to={CampaignReport.path}
            label={translate("crm.metaOrigin.report.nav")}
            Icon={BarChart3}
            isActive={currentPath === CampaignReport.path}
          />
        </CanAccess>
        {/* Almikia Epic EW2 (TASK-080): direccion and lead_manager only; an asesora has no
            Inbox entry (she sees the badges on her own leads). */}
        <CanAccess resource="whatsapp_inbox" action="list">
          <RailLink
            to={InboxScreen.path}
            label={translate("crm.whatsapp.inbox.nav")}
            Icon={MessageCircle}
            isActive={currentPath === InboxScreen.path}
          />
        </CanAccess>
        {/* Almikia Epic EH (TASK-013): every role that can read sales tools. */}
        <CanAccess resource="sales_tools" action="list">
          <RailLink
            to={SalesToolLibrary.path}
            label={translate("crm.salesTools.nav")}
            Icon={BookOpen}
            isActive={currentPath === SalesToolLibrary.path}
          />
        </CanAccess>
        {/* Almikia Epic EQ (TASK-018): payment plan templates, direccion only. */}
        <CanAccess resource="quote_plan_templates" action="create">
          <RailLink
            to={PlanTemplateList.path}
            label={translate("crm.quoter.templates.nav")}
            Icon={Wallet}
            isActive={currentPath === PlanTemplateList.path}
          />
        </CanAccess>
        {/* Almikia Epic EQ2 (TASK-099): quoter configuration and panel, direccion only. */}
        <CanAccess resource="quoter_configs" action="create">
          <RailLink
            to={QuoterConfigPage.path}
            label={translate("crm.quoter.config.nav")}
            Icon={SlidersHorizontal}
            isActive={currentPath === QuoterConfigPage.path}
          />
        </CanAccess>
        <CanAccess resource="quoter_unit_details" action="edit">
          <RailLink
            to={QuoterPanel.path}
            label={translate("crm.quoter.panel.nav")}
            Icon={Gauge}
            isActive={currentPath === QuoterPanel.path}
          />
        </CanAccess>
        {/* Almikia batch EQ3 (TASK-115): the approver's list (direccion; an
            approver of another role reaches it from the notice in the lead's
            quoter panel). */}
        <CanAccess resource="quoter_plan_approvers" action="list">
          <RailLink
            to={PlansToApprove.path}
            label={translate("crm.quoter.approval.list_title")}
            Icon={ClipboardCheck}
            isActive={currentPath === PlansToApprove.path}
          />
        </CanAccess>
      </nav>
      {/* Almikia Epic PL (TASK-061): every role, no gate. */}
      <div className="px-2 shrink-0">
        <FeedbackButton variant="desktop" />
      </div>
      <div className="flex flex-row items-center justify-center gap-1 px-2 py-3 shrink-0">
        <ThemeModeToggle className="focus-visible:ring-brand-yellow" />
        <RefreshButton className="focus-visible:ring-brand-yellow" />
        <UserMenu className="focus-visible:ring-brand-yellow">
          <ProfileMenu />
          <CanAccess resource="sales" action="list">
            <UsersMenu />
          </CanAccess>
          <CanAccess resource="configuration" action="edit">
            <SettingsMenu />
          </CanAccess>
          <ImportFromJsonMenuItem />
          <ChangelogMenuItem />
        </UserMenu>
      </div>
    </header>
  );
};

const RailLink = ({
  to,
  label,
  Icon,
  isActive,
}: {
  to: string;
  label: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  isActive: boolean;
}) => (
  <Link
    to={to}
    aria-current={isActive ? "page" : undefined}
    className={cn(
      "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
      "focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-brand-yellow",
      isActive
        ? "bg-sidebar-accent/40 text-brand-yellow"
        : "text-sidebar-foreground/80 hover:bg-sidebar-accent/20 hover:text-sidebar-foreground",
    )}
  >
    <Icon className="size-5 shrink-0" />
    <span className="truncate">{label}</span>
  </Link>
);

const UsersMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<UsersMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/sales" className="flex items-center gap-2">
        <Users />
        {translate("resources.sales.name", { smart_count: 2 })}
      </Link>
    </DropdownMenuItem>
  );
};

const ProfileMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ProfileMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/profile" className="flex items-center gap-2">
        <User />
        {translate("crm.profile.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const SettingsMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<SettingsMenu> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/settings" className="flex items-center gap-2">
        <Settings />
        {translate("crm.settings.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const ImportFromJsonMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ImportFromJsonMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ImportPage.path} className="flex items-center gap-2">
        <Import />
        {translate("crm.header.import_data")}
      </Link>
    </DropdownMenuItem>
  );
};

const ChangelogMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ChangelogMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ChangelogPage.path} className="flex items-center gap-2">
        <FileText />
        {translate("crm.changelog.title")}
      </Link>
    </DropdownMenuItem>
  );
};
export default Header;
